import React, { useEffect, useState } from 'react'
import { PaginatedResponse, PressReleaseLight } from '@finance-lab/utils/types'
import { PressReleaseApi } from '../../../../api/PressReleasesApi'
import Card from '@mui/material/Card'
import List from '@mui/material/List'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import { formatDateYYYYMMDD } from '../../../../packages/utils/numberFormatters'
import ListItemButton from '@mui/material/ListItemButton'
import { Link } from 'react-router-dom'
import { getNewsLink } from '../../../../utils/get-news-link'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { PressReleaseAvatar } from '../../../../components/entities/PressReleaseAvatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { PressReleasesFilter } from '../../../press-releases/PressReleasesFilter'
import { useFilterSettings } from '../../../press-releases/utils'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'

const pressReleaseApi = new PressReleaseApi()

export const CompanyPressReleases = ({ companyId, currentPrId }: { companyId: string; currentPrId?: string }) => {
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<Omit<PressReleaseLight, 'body'>>>({
        totalCount: 0,
        data: [],
    })
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    })
    const isFirstPage = (paginationModel.page + 1) * paginationModel.pageSize > pageInfo.totalCount
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const { endDate, senderType, searchValue, showOnlyReports, showOnlyWithRelatedTransactions } = useFilterSettings()

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data, totalCount } = await pressReleaseApi.allPressReleases(paginationModel, {
                    companyId,
                    maxPublicationDate: endDate ?? undefined,
                    senderType: senderType,
                    showOnlyReports,
                    showOnlyWithRelatedTransactions,
                    search: searchValue,
                })

                setPageInfo({ data: data.map(({ body, ...pr }) => pr), totalCount })
            } catch (err) {
                setHasError(true)
            } finally {
                setIsLoading(false)
            }
        }

        void fetchData()
        setIsLoading(true)
        setHasError(false)
    }, [companyId, endDate, paginationModel, searchValue, senderType, showOnlyReports, showOnlyWithRelatedTransactions])

    return (
        <Card>
            <CardContent sx={{ padding: 0 }}>
                <Typography variant={'h4'} sx={{ margin: 4, marginBottom: 2 }}>
                    Pressmeddelanden
                </Typography>

                <Box sx={{ marginX: 4 }}>
                    <PressReleasesFilter isSlim />
                </Box>

                <Divider sx={{ marginTop: 4 }} />
                <LinearProgress sx={{ visibility: isLoading ? 'visible' : 'hidden' }} />

                {hasError && <p>Någonting gick fel, pressmeddelandena kunde inte hämtas.</p>}

                <List>
                    {pageInfo.data?.map((pr) => (
                        <ListItemButton
                            key={pr.id}
                            alignItems='flex-start'
                            selected={pr.id === currentPrId}
                            LinkComponent={Link}
                            to={getNewsLink(companyId, pr.id)}
                        >
                            <ListItemAvatar>
                                <PressReleaseAvatar pressRelease={pr} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={pr.title}
                                slotProps={{
                                    primary: {
                                        sx: {
                                            display: '-webkit-box',
                                            WebkitLineClamp: '2',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                        },
                                        title: pr.title,
                                        variant: 'body1',
                                    },
                                }}
                                secondary={
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <div>{formatDateYYYYMMDD(pr.publishedAt)}</div>
                                    </Box>
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>

                <Box sx={{ display: 'flex', paddingX: 2 }}>
                    {!isFirstPage && (
                        <Button
                            onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 })}
                            sx={{ justifySelf: 'flex-start' }}
                        >
                            Föregående
                        </Button>
                    )}
                    {paginationModel.page > 0 && (
                        <Button
                            onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 })}
                            sx={{ marginLeft: 'auto' }}
                        >
                            Senare
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}
